import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Container,
  Stack,
  Typography,
  Button,
  Divider,
  Grid,
  TextField,
  InputAdornment,
  Switch,
  Autocomplete,
  Box,
  Radio,
  CircularProgress,
  Checkbox,
  Tooltip,
} from "@common-ui/ui";
import {
  Email,
  Tv,
  AlternateEmail,
  Phone,
  PhoneAndroid,
  LinkedIn,
  Facebook,
  Twitter,
  Link,
  Info,
} from "@mui/icons-material";
import { FormControlLabel } from "@mui/material";
import Address from "../../org/Address";
import { countries, updateUserProfile } from "../../../services";
import MandatoryIcon from "../../../components/MandatoryIcon";
import { organizationSlice } from "../../../reducers/organizationReducer";

const ProfilePage = ({
  email,
  isLoading,
  mode,
  setMode,
  userData,
  setRefreshData,
  setToastOpen,
  defaultOrg,
  orgs,
}) => {
  const dispatch = useDispatch();
  let orgsList = useSelector((state) => state.organization.organizations);

  const salutationsOptions = [
    {
      label: "Mr.",
      value: "Mr.",
    },
    {
      label: "Mrs.",
      value: "Mrs.",
    },
    {
      label: "Ms.",
      value: "Ms.",
    },
    {
      label: "Dr.",
      value: "Dr.",
    },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: "",
      last_name: "",
      middle_initial: "",
      salutation: null,
      name_private: false,
      org: "",
      email: "",
      display_name: "",
      email_private: false,
      email_notification: false,
      recovery_email: "",
      office_phone: "",
      office_phone_private: false,
      mobile_phone: "",
      mobile_phone_private: false,
      mobile_phone_sms_notification: false,
      webpage: "",
      facebook: "",
      linkedin: "",
      twitter: "",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: null,
      postal_code: "",
      mailing_address_private: false,
      shipping_address_line_1: "",
      shipping_address_line_2: "",
      shipping_city: "",
      shipping_state: "",
      shipping_country: null,
      shipping_postal_code: "",
      shipping_address_private: false,
      shipping_same_as_mailing: false,
    },
    validationSchema: Yup.object({
      first_name: Yup.string().trim().max(100).required(),
      last_name: Yup.string().trim().max(100).required(),
      middle_initial: Yup.string().nullable().trim().length(1),
      salutation: Yup.object().nullable(),
      name_private: Yup.boolean(),
      org: Yup.object().nullable(),
      display_name: Yup.string()
        .nullable()
        .trim()
        .min(3, "Display name cannot be less than 3 characters long")
        .max(20, "Display name cannot be more than 20 characters long"),
      email_private: Yup.boolean(),
      email_notification: Yup.boolean(),
      recovery_email: Yup.string().nullable().email().trim(),
      office_phone: Yup.string()
        .nullable()
        .trim()
        .matches(/^[0-9]*$/),
      office_phone_private: Yup.boolean(),
      mobile_phone: Yup.string()
        .nullable()
        .trim()
        .matches(/^[0-9]*$/),
      mobile_phone_private: Yup.boolean(),
      mobile_phone_sms_notification: Yup.boolean(),
      webpage: Yup.string().nullable().url().trim(),
      facebook: Yup.string().nullable().url().trim(),
      linkedin: Yup.string().nullable().url().trim(),
      twitter: Yup.string().nullable().url().trim(),
      address_line_1: Yup.string().nullable().trim(),
      address_line_2: Yup.string().nullable().trim(),
      city: Yup.string().nullable().trim(),
      state: Yup.string().nullable().trim(),
      country: Yup.object().nullable(),
      postal_code: Yup.string().nullable().trim(),
      shipping_address_line_1: Yup.string().nullable().trim(),
      shipping_address_line_2: Yup.string().nullable().trim(),
      shipping_city: Yup.string().nullable().trim(),
      shipping_state: Yup.string().nullable().trim(),
      shipping_country: Yup.object().nullable(),
      shipping_postal_code: Yup.string().nullable().trim(),
      shipping_same_as_mailing: Yup.boolean(),
    }),
    onSubmit: () => {
      let {
        first_name,
        last_name,
        middle_initial,
        salutation,
        name_private,
        org,
        display_name,
        email_private,
        email_notification,
        recovery_email,
        office_phone,
        office_phone_private,
        mobile_phone,
        mobile_phone_private,
        mobile_phone_sms_notification,
        webpage,
        facebook,
        linkedin,
        twitter,
        mailing_address_private,
        address_line_1,
        address_line_2,
        city,
        state,
        country,
        postal_code,
        shipping_address_private,
        shipping_address_line_1,
        shipping_address_line_2,
        shipping_city,
        shipping_state,
        shipping_country,
        shipping_postal_code,
        shipping_same_as_mailing,
      } = formik.values;

      let attributes = {
        first_name,
        last_name,
        middle_initial: middle_initial ?? " ",
        salutation: salutation?.label || " ",
        name_private: name_private,
        display_name,
        org: org?.id ?? "",
        email_private,
        email_notification,
        recovery_email: recovery_email ?? " ",
        office_phone: office_phone ? office_phone.trim() : "",
        office_phone_private,
        mobile_phone: mobile_phone ? mobile_phone.trim() : "",
        mobile_phone_private,
        mobile_phone_sms_notification,
        webpage: webpage ?? " ",
        facebook: facebook ?? " ",
        linkedin: linkedin ?? " ",
        twitter: twitter ?? " ",
        mailing_address_private: mailing_address_private,
        shipping_address_private: shipping_address_private,
        shipping_same_as_mailing: shipping_same_as_mailing,
        mailing_address: {
          address_line_1: address_line_1 ? address_line_1 : " ",
          address_line_2: address_line_2 ? address_line_2 : " ",
          city: city ? city : " ",
          state_or_province: state ? state : " ",
          country_or_region: country?.label || " ",
          postal_code: postal_code ? postal_code : " ",
        },
        shipping_address: {
          address_line_1: shipping_address_line_1
            ? shipping_address_line_1
            : " ",
          address_line_2: shipping_address_line_2
            ? shipping_address_line_2
            : " ",
          city: shipping_city ? shipping_city : " ",
          state_or_province: shipping_state ? shipping_state : " ",
          country_or_region: shipping_country?.label || " ",
          postal_code: shipping_postal_code ? shipping_postal_code : " ",
        },
      };

      // TODO: pass mailing_address and shipping_address as null to delete them

      if (!address_line_1 && !city && !state && !country && !postal_code) {
        attributes.mailing_address = null;
      } else {
        attributes.mailing_address = {
          address_line_1: address_line_1,
          address_line_2: address_line_2,
          city: city,
          state_or_province: state,
          country_or_region: country?.label,
          postal_code: postal_code,
        };
      }
      if (
        !shipping_address_line_1 &&
        !shipping_city &&
        !shipping_state &&
        !shipping_country &&
        !shipping_postal_code
      ) {
        attributes.shipping_address = null;
      } else {
        attributes.shipping_address = {
          address_line_1: shipping_address_line_1,
          address_line_2: shipping_address_line_2,
          city: shipping_city,
          state_or_province: shipping_state,
          country_or_region: shipping_country?.label,
          postal_code: shipping_postal_code,
        };
      }

      submit(attributes);
    },
  });

  const submit = async (attributes) => {
    try {
      attributes = Object.keys(attributes).reduce((acc, key) => {
        acc[key] =
          typeof attributes[key] === "string" && attributes[key] === ""
            ? " "
            : attributes[key];
        return acc;
      }, {});

      await updateUserProfile(attributes);
      dispatch(
        organizationSlice.actions.setDefaultOrganization(
          orgsList.find((org) => org.id === attributes.org)
        )
      );
      setRefreshData(true);
      setToastOpen(true);
    } catch (error) {
      alert(error);
      setRefreshData(true);
      setToastOpen(false);
      // setIsSuccess(false)
    }
  };

  const setFormValues = () => {
    formik.setFieldValue("first_name", userData?.first_name?.trim());
    formik.setFieldValue("last_name", userData?.last_name?.trim());
    formik.setFieldValue("middle_initial", userData?.middle_initial?.trim());
    formik.setFieldValue(
      "salutation",
      salutationsOptions.find((option) => option.label === userData?.salutation)
    );
    formik.setFieldValue(
      "org",
      orgs.find((option) => {
        return option.id === defaultOrg;
      })
    );
    formik.setFieldValue(
      "name_private",
      userData?.name_private ? userData?.name_private : false
    );
    formik.setFieldValue("email", email);
    formik.setFieldValue("display_name", userData?.display_name?.trim());
    formik.setFieldValue(
      "email_private",
      userData?.email_private ? userData?.email_private : false
    );
    formik.setFieldValue(
      "email_notification",
      userData?.email_notification ? userData?.email_notification : false
    );
    formik.setFieldValue("recovery_email", userData?.recovery_email?.trim());
    formik.setFieldValue("office_phone", userData?.office_phone?.trim());
    formik.setFieldValue(
      "office_phone_private",
      userData?.office_phone_private ? userData?.office_phone_private : false
    );
    formik.setFieldValue("mobile_phone", userData?.mobile_phone?.trim());
    formik.setFieldValue(
      "mobile_phone_private",
      userData?.mobile_phone_private ? userData?.mobile_phone_private : false
    );
    formik.setFieldValue(
      "mobile_phone_sms_notification",
      userData?.mobile_phone_sms_notification
        ? userData?.mobile_phone_sms_notification
        : false
    );
    formik.setFieldValue("webpage", userData?.webpage?.trim());
    formik.setFieldValue("facebook", userData?.facebook?.trim());
    formik.setFieldValue("linkedin", userData?.linkedin?.trim());
    formik.setFieldValue("twitter", userData?.twitter?.trim());
    formik.setFieldValue(
      "mailing_address_private",
      userData?.mailing_address_private
        ? userData?.mailing_address_private
        : false
    );
    formik.setFieldValue(
      "shipping_address_private",
      userData?.shipping_address_private
        ? userData?.shipping_address_private
        : false
    );
    if (userData?.mailing_address === null) {
      formik.setFieldValue("address_line_1", "");
      formik.setFieldValue("address_line_2", "");
      formik.setFieldValue("city", "");
      formik.setFieldValue("state", "");
      formik.setFieldValue("country", null);
      formik.setFieldValue("postal_code", "");
    } else {
      formik.setFieldValue(
        "address_line_1",
        userData?.mailing_address?.address_line_1?.trim()
      );
      formik.setFieldValue(
        "address_line_2",
        userData?.mailing_address?.address_line_2.trim()
      );
      formik.setFieldValue("city", userData?.mailing_address?.city.trim());
      formik.setFieldValue(
        "state",
        userData?.mailing_address?.state_or_province.trim()
      );
      formik.setFieldValue(
        "country",
        countries.find(
          (country) =>
            country.label === userData?.mailing_address?.country_or_region
        )
      );
      formik.setFieldValue(
        "postal_code",
        userData?.mailing_address?.postal_code.trim()
      );
    }

    if (userData?.shipping_address === null) {
      formik.setFieldValue("shipping_address_line_1", "");
      formik.setFieldValue("shipping_address_line_2", "");
      formik.setFieldValue("shipping_city", "");
      formik.setFieldValue("shipping_state", "");
      formik.setFieldValue("shipping_country", null);
      formik.setFieldValue("shipping_postal_code", "");
    } else {
      formik.setFieldValue(
        "shipping_address_line_1",
        userData?.shipping_address?.address_line_1.trim()
      );
      formik.setFieldValue(
        "shipping_address_line_2",
        userData?.shipping_address?.address_line_2.trim()
      );
      formik.setFieldValue(
        "shipping_city",
        userData?.shipping_address?.city.trim()
      );
      formik.setFieldValue(
        "shipping_state",
        userData?.shipping_address?.state_or_province.trim()
      );
      formik.setFieldValue(
        "shipping_country",
        countries.find(
          (country) =>
            country.label === userData?.shipping_address?.country_or_region
        )
      );
      formik.setFieldValue(
        "shipping_postal_code",
        userData?.shipping_address?.postal_code.trim()
      );
      formik.setFieldValue(
        "shipping_same_as_mailing",
        userData?.shipping_same_as_mailing
          ? userData?.shipping_same_as_mailing
          : false
      );
    }
  };

  useEffect(() => {
    setToastOpen(false);
    setFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    setFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (formik.values?.shipping_same_as_mailing) {
      formik.setFieldValue(
        "shipping_address_line_1",
        formik.values.address_line_1
      );
      formik.setFieldValue(
        "shipping_address_line_2",
        formik.values.address_line_2
      );
      formik.setFieldValue("shipping_city", formik.values.city);
      formik.setFieldValue("shipping_state", formik.values.state);
      formik.setFieldValue("shipping_postal_code", formik.values.postal_code);
      formik.setFieldValue("shipping_country", formik.values.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.shipping_same_as_mailing,
    formik.values.address_line_1,
    formik.values.address_line_2,
    formik.values.city,
    formik.values.state,
    formik.values.country,
    formik.values.postal_code,
  ]);

  return (
    <>
      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 1 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ my: 3 }}
            >
              <Typography variant="h4">Full Name</Typography>
              <FormControlLabel
                control={<Switch />}
                label="Private"
                name="name_private"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name_private}
                checked={formik.values.name_private}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              //   alignItems="center"
              justifyContent="space-between"
            >
              <div style={{ minWidth: "10%", maxWidth: "15%" }}>
                <Typography>Salutation</Typography>
                <Autocomplete
                  id="salutations-select"
                  sx={{ mt: 1 }}
                  disablePortal
                  ListboxProps={{ style: { maxHeight: "200px" } }}
                  options={salutationsOptions}
                  autoHighlight
                  size="small"
                  getOptionLabel={(option) => option.label}
                  margin="normal"
                  name="salutation"
                  limitTags={4}
                  onBlur={formik.handleBlur}
                  onChange={(value, newValue) => {
                    formik.setFieldValue("salutation", newValue);
                  }}
                  fullWidth
                  value={formik.values.salutation}
                  //   defaultValue={formik.values.salutation}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      {...props}
                    >
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select preferred salutation"
                      helperText={
                        formik.touched.salutation && formik.errors.salutation
                      }
                      error={Boolean(
                        formik.touched.salutation && formik.errors.salutation
                      )}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: "off", // disable autocomplete and autofill
                      }}
                    />
                  )}
                />
              </div>
              <div style={{ minWidth: "30%" }}>
                <Typography>
                  <MandatoryIcon />
                  First Name
                </Typography>
                {mode === "edit" ? (
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.first_name && formik.errors.first_name
                    )}
                    fullWidth
                    helperText={
                      formik.touched.first_name && formik.errors.first_name
                    }
                    placeholder="Type"
                    margin="normal"
                    name="first_name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.first_name}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="h4">
                    {formik.values.first_name}
                  </Typography>
                )}
              </div>
              <div style={{ maxWidth: "10%" }}>
                <Typography>Middle Initial</Typography>
                {mode === "edit" ? (
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.middle_initial &&
                        formik.errors.middle_initial
                    )}
                    fullWidth
                    helperText={
                      formik.touched.middle_initial &&
                      formik.errors.middle_initial
                    }
                    placeholder="Type"
                    margin="normal"
                    name="middle_initial"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.middle_initial}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="h3">
                    {formik.values.middle_initial}
                  </Typography>
                )}
              </div>
              <div style={{ minWidth: "30%" }}>
                <Typography>
                  <MandatoryIcon />
                  Last Name
                </Typography>
                <TextField
                  sx={{ mt: 1 }}
                  size="small"
                  autoComplete="off"
                  error={Boolean(
                    formik.touched.last_name && formik.errors.last_name
                  )}
                  fullWidth
                  helperText={
                    formik.touched.last_name && formik.errors.last_name
                  }
                  placeholder="Type"
                  margin="normal"
                  name="last_name"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.last_name}
                  variant="outlined"
                />
              </div>
            </Stack>
            <Divider sx={{ py: 1 }} />
          </Container>
          <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
            <Stack direction="column">
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <div>
                    <Typography variant="h4">Default Organization</Typography>
                    <Autocomplete
                      id="country-select"
                      disablePortal
                      disabled={!orgs?.length}
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={orgs}
                      autoHighlight
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      margin="normal"
                      name="org"
                      limitTags={4}
                      onBlur={formik.handleBlur}
                      onChange={(value, newValue) => {
                        formik.setFieldValue("org", newValue);
                      }}
                      fullWidth
                      value={formik.values.org}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          {option?.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select"
                          margin="normal"
                          sx={{ mt: 1 }}
                          helperText={formik.touched.org && formik.errors.org}
                          error={Boolean(
                            formik.touched.org && formik.errors.org
                          )}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "off", // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </div>
                </Grid>
              </Grid>
            </Stack>
            <Divider sx={{ py: 1 }} />
          </Container>
          <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
            <Typography variant="h4" sx={{ my: 3 }}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>Email</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    placeholder="Type"
                    margin="normal"
                    name="email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="email"
                    value={formik.values.email}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>Website</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Link />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.webpage && formik.errors.webpage
                    )}
                    fullWidth
                    helperText={formik.touched.webpage && formik.errors.webpage}
                    placeholder="Type"
                    margin="normal"
                    name="webpage"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="website"
                    value={formik.values.webpage}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>
                    Display Name{" "}
                    <span>
                      <Tooltip
                        placement="right"
                        arrow
                        title="You can choose any display name you want,
                        using a combination of letters, underscores “_” or dashes “-”.
                        The display name is how you will appear to other users,
                        so keep it business and family friendly.
                        Your username is the default display name"
                      >
                        <Info sx={{ fontSize: 14, color: "#777777" }} />
                      </Tooltip>
                    </span>
                  </Typography>
                  <Tooltip
                    title="Your display name is how you will appear to other users in comments, posts, and events."
                    placement="bottom"
                    arrow
                  >
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tv />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.display_name &&
                          formik.errors.display_name
                      )}
                      fullWidth
                      helperText={
                        formik.touched.display_name &&
                        formik.errors.display_name
                      }
                      placeholder="Type"
                      margin="normal"
                      name="display_name"
                      onBlur={formik.handleBlur}
                      onChange={(event) => {
                        if (
                          // eslint-disable-next-line no-useless-escape
                          /\d|[`!@#$%^&*()+\=\[\]{};':"\\|,.<>\/?~]/.test(
                            event.target?.value
                          )
                        ) {
                          formik.setFieldError(
                            "display_name",
                            "Display Name cannot have numbers or special characters other than '-' or '_'"
                          );
                        } else {
                          formik.setFieldValue(
                            "display_name",
                            event.target.value
                          );
                        }
                      }}
                      value={formik.values.display_name}
                      variant="outlined"
                    />
                  </Tooltip>
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>LinkedIn</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <LinkedIn />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.linkedin && formik.errors.linkedin
                    )}
                    fullWidth
                    helperText={
                      formik.touched.linkedin && formik.errors.linkedin
                    }
                    placeholder="Type"
                    margin="normal"
                    name="linkedin"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.linkedin}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>Recovery Email</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <AlternateEmail />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.recovery_email &&
                        formik.errors.recovery_email
                    )}
                    fullWidth
                    helperText={
                      formik.touched.recovery_email &&
                      formik.errors.recovery_email
                    }
                    placeholder="Type"
                    margin="normal"
                    name="recovery_email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="email"
                    value={formik.values.recovery_email}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>Facebook</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Facebook />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.facebook && formik.errors.facebook
                    )}
                    fullWidth
                    helperText={
                      formik.touched.facebook && formik.errors.facebook
                    }
                    placeholder="Type"
                    margin="normal"
                    name="facebook"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="website"
                    value={formik.values.facebook}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography>Office Phone</Typography>
                    <FormControlLabel
                      control={<Switch />}
                      label="Private"
                      name="office_phone_private"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.office_phone_private}
                      checked={formik.values.office_phone_private}
                    />
                  </Stack>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.office_phone && formik.errors.office_phone
                    )}
                    fullWidth
                    helperText={
                      formik.touched.office_phone && formik.errors.office_phone
                    }
                    placeholder="Type"
                    margin="normal"
                    name="office_phone"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="string"
                    value={formik.values.office_phone}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Typography>Twitter</Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Twitter />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.twitter && formik.errors.twitter
                    )}
                    fullWidth
                    helperText={formik.touched.twitter && formik.errors.twitter}
                    placeholder="Type"
                    margin="normal"
                    name="twitter"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="website"
                    value={formik.values.twitter}
                    variant="outlined"
                  />
                </div>
              </Grid>

              <Grid item md={6} xs={12}>
                {" "}
                <div>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography>Mobile</Typography>
                    <FormControlLabel
                      control={<Switch />}
                      label="Private"
                      name="mobile_phone_private"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.mobile_phone_private}
                      checked={formik.values.mobile_phone_private}
                    />
                  </Stack>
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <PhoneAndroid />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    error={Boolean(
                      formik.touched.mobile_phone && formik.errors.mobile_phone
                    )}
                    fullWidth
                    helperText={
                      formik.touched.mobile_phone && formik.errors.mobile_phone
                    }
                    placeholder="Type"
                    margin="normal"
                    name="mobile_phone"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="string"
                    value={formik.values.mobile_phone}
                    variant="outlined"
                  />
                </div>
              </Grid>
            </Grid>
            <Divider sx={{ py: 1 }} />
          </Container>
          <Container maxWidth="md" component="main" sx={{ mt: 3 }}>
            <Typography variant="h4" sx={{ my: 3 }}>
              Addresses
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h5">Mailing Address</Typography>
                  <FormControlLabel
                    control={<Switch />}
                    label="Private"
                    name="mailing_address_private"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mailing_address_private}
                    checked={formik.values.mailing_address_private}
                  />
                </Stack>
                <Stack direction="column">
                  <div>
                    <Typography sx={{ mt: 3 }}>Address 1</Typography>
                    <Address formik={formik} field="address_line_1" />
                  </div>
                  {/* </Container> */}
                  {/* <Container> */}
                  <Typography sx={{ mt: 1 }}>Address 2</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(
                      formik.touched.address_line_2 &&
                        formik.errors.address_line_2
                    )}
                    fullWidth
                    helperText={
                      formik.touched.address_line_2 &&
                      formik.errors.address_line_2
                    }
                    placeholder="Type"
                    // label="Company or Organization Name"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="address_line_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address_line_2}
                  />
                  <div>
                    <Typography>City</Typography>
                    <Address formik={formik} field="city" />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>State/Province</Typography>
                    <Address formik={formik} field="state" />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>Country / Region</Typography>
                    <Autocomplete
                      id="country"
                      sx={{ mt: 1 }}
                      disablePortal
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={countries}
                      autoHighlight
                      size="small"
                      getOptionLabel={(option) => option.label}
                      margin="normal"
                      name="country"
                      limitTags={4}
                      onBlur={formik.handleBlur}
                      onChange={(value, newValue) => {
                        formik.setFieldValue("country", newValue);
                      }}
                      fullWidth
                      value={formik.values.country}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          <img
                            loading="lazy"
                            width="20"
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            alt=""
                          />
                          {option.label} ({option.code}) +{option.phone}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select"
                          helperText={
                            formik.touched.country && formik.errors.country
                          }
                          error={Boolean(
                            formik.touched.country && formik.errors.country
                          )}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "off", // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>Zip/Postal Code</Typography>
                    <Address formik={formik} field="postal_code" />
                  </div>
                </Stack>
                {/* </Container> */}
                {/* </Grid> */}
              </Grid>
              <Grid item md={6} xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h5">Shipping Address</Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      label="Same as Mailing address"
                      name="shipping_same_as_mailing"
                      onBlur={formik.handleBlur}
                      onChange={(event) => {
                        formik.setFieldValue(
                          "shipping_same_as_mailing",
                          event.target.checked
                        );
                      }}
                      value={formik.values.shipping_same_as_mailing}
                      checked={formik.values.shipping_same_as_mailing}
                      sx={{ mr: 0.5 }}
                    />
                    <FormControlLabel
                      control={<Switch />}
                      label="Private"
                      name="shipping_address_private"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.shipping_address_private}
                      checked={formik.values.shipping_address_private}
                    />
                  </Stack>
                </Stack>
                <Stack direction="column">
                  <div>
                    <Typography sx={{ mt: 3 }}>Address 1</Typography>
                    <Address formik={formik} field="shipping_address_line_1" />
                  </div>
                  {/* </Container> */}
                  {/* <Container> */}
                  <Typography sx={{ mt: 1 }}>Address 2</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(
                      formik.touched.shipping_address_line_2 &&
                        formik.errors.shipping_address_line_2
                    )}
                    fullWidth
                    helperText={
                      formik.touched.shipping_address_line_2 &&
                      formik.errors.shipping_address_line_2
                    }
                    placeholder="Type"
                    // label="Company or Organization Name"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="shipping_address_line_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.shipping_address_line_2}
                  />
                  <div>
                    <Typography>City</Typography>
                    <Address formik={formik} field="shipping_city" />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>State/Province</Typography>
                    <Address formik={formik} field="shipping_state" />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>Country / Region</Typography>
                    <Autocomplete
                      id="country"
                      sx={{ mt: 1 }}
                      disablePortal
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={countries}
                      autoHighlight
                      size="small"
                      getOptionLabel={(option) => option.label}
                      margin="normal"
                      name="shipping_country"
                      limitTags={4}
                      onBlur={formik.handleBlur}
                      onChange={(value, newValue) => {
                        formik.setFieldValue("shipping_country", newValue);
                      }}
                      fullWidth
                      value={formik.values.shipping_country}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          <img
                            loading="lazy"
                            width="20"
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            alt=""
                          />
                          {option.label} ({option.code}) +{option.phone}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select"
                          helperText={
                            formik.touched.shipping_country &&
                            formik.errors.shipping_country
                          }
                          error={Boolean(
                            formik.touched.shipping_country &&
                              formik.errors.shipping_country
                          )}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "off", // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <Typography sx={{ mt: 1 }}>Zip/Postal Code</Typography>
                    <Address formik={formik} field="shipping_postal_code" />
                  </div>
                </Stack>
              </Grid>
            </Grid>
            <Divider sx={{ py: 1 }} />
          </Container>
          <Container maxWidth="md" component="main">
            <Typography variant="h4" sx={{ my: 3 }}>
              Notification Settings
            </Typography>
            <Stack direction="column">
              <Typography variant="h5">Receive email communications</Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ maxWidth: "30%" }}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes (opt-in)"
                  name="email_notification"
                  onBlur={formik.handleBlur}
                  onChange={() =>
                    formik.setFieldValue("email_notification", true)
                  }
                  value={true}
                  checked={formik.values.email_notification}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No (opt-out)"
                  name="email_notification"
                  onBlur={formik.handleBlur}
                  onChange={() =>
                    formik.setFieldValue("email_notification", false)
                  }
                  value={false}
                  checked={!formik.values.email_notification}
                />
              </Stack>
              <Typography variant="h5">
                Receive SMS text communications
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ maxWidth: "30%" }}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Yes (opt-in)"
                  name="mobile_phone_sms_notification"
                  onBlur={formik.handleBlur}
                  onChange={() =>
                    formik.setFieldValue("mobile_phone_sms_notification", true)
                  }
                  value={true}
                  checked={formik.values.mobile_phone_sms_notification}
                />
                <FormControlLabel
                  control={<Radio />}
                  label="No (opt-out)"
                  name="mobile_phone_sms_notification"
                  onBlur={formik.handleBlur}
                  onChange={() =>
                    formik.setFieldValue("mobile_phone_sms_notification", false)
                  }
                  value={false}
                  checked={!formik.values.mobile_phone_sms_notification}
                />
              </Stack>
            </Stack>
          </Container>
          <Container maxWidth="md" component="main">
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={{ xs: 1, md: 2 }}
              sx={{ py: 2 }}
            >
              <div>
                <Button
                  color="primary"
                  fullWidth
                  size="large"
                  type="reset"
                  onClick={() => {
                    formik.resetForm();
                    //   setFormValues();
                    setMode("view");
                  }}
                  variant="text"
                >
                  Cancel
                </Button>
              </div>
              <div>
                <Button
                  color="primary"
                  disabled={
                    isLoading ||
                    formik.isSubmitting ||
                    !(formik.isValid && formik.dirty)
                  }
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Save
                </Button>
              </div>
            </Stack>
          </Container>
        </form>
      )}
    </>
  );
};

export default ProfilePage;
